import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Select, MenuItem, TextField, Stack, InputAdornment, Divider,
} from '@mui/material';
import {
  Sun, Moon, Star, Flame, Sparkles, Clock, Bell, Heart,
  CalendarDays, BookOpen, Zap, Sunrise, MoonStar, Sunset, Wheat, TreePalm, Wine, Droplets,
} from 'lucide-react';
import { ShabbatCandles } from '../../../business/widgetIconMap';
import ZmanSelect from '../components/ZmanSelect';

const navy = '#1e3a5f';

// sentinel keys — no manual offset; resolved in fetchMoadActions using global entryZman/exitZman
const MOAD_DEFAULT_KEYS = new Set(['dayEntryTime', 'dayExitTime', 'dayEntrySunset', 'dayExitSunset']);



const ICON_OPTIONS = [
  { value: 'ShabbatCandles', label: 'נרות', Icon: ShabbatCandles },
  { value: 'Flame',          label: 'נר',    Icon: Flame          },
  { value: 'Sunset',         label: 'שקיעה', Icon: Sunset         },
  { value: 'MoonStar',       label: 'כוכבים',Icon: MoonStar       },
  { value: 'Moon',           label: 'ירח',   Icon: Moon           },
  { value: 'Sun',            label: 'שמש',   Icon: Sun            },
  { value: 'Sunrise',        label: 'זריחה', Icon: Sunrise        },
  { value: 'Star',           label: 'כוכב',  Icon: Star           },
  { value: 'Sparkles',       label: 'ניצוץ', Icon: Sparkles       },
  { value: 'Clock',          label: 'שעון',  Icon: Clock          },
  { value: 'Wine',           label: 'יין',   Icon: Wine           },
  { value: 'Wheat',          label: 'חיטה',  Icon: Wheat          },
  { value: 'TreePalm',       label: 'דקל',   Icon: TreePalm       },
  { value: 'Droplets',       label: 'מים',   Icon: Droplets       },
  { value: 'Bell',           label: 'פעמון', Icon: Bell           },
  { value: 'Heart',          label: 'לב',    Icon: Heart          },
  { value: 'CalendarDays',   label: 'לוח',   Icon: CalendarDays   },
  { value: 'BookOpen',       label: 'ספר',   Icon: BookOpen       },
  { value: 'Zap',            label: 'ברק',   Icon: Zap            },
];

const selectSx = {
  fontSize: '0.8rem', bgcolor: 'white',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
};

const IconSelectItem = ({ iconValue }) => {
  const opt = ICON_OPTIONS.find(o => o.value === iconValue);
  if (!opt) return null;
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <opt.Icon size={14} color="#374151" />
      <Typography sx={{ fontSize: '0.78rem' }}>{opt.label}</Typography>
    </Stack>
  );
};

// All 27 keys always returned by Hebcal zmanim API (same for every date) + our internal keys.
// Only keys NOT in this set will appear in the "unique to this day" section.
const KNOWN_STATIC_KEYS = new Set([
  // sentinel keys (resolved via global entry/exit zman config)
  'dayEntryTime', 'dayExitTime', 'dayEntrySunset', 'dayExitSunset',
  // all 27 standard Hebcal zmanim API keys (identical response for every date)
  'chatzotNight', 'alotHaShachar', 'misheyakir', 'misheyakirMachmir',
  'dawn', 'sunrise',
  'sofZmanShmaMGA19Point8', 'sofZmanShmaMGA16Point1', 'sofZmanShmaMGA', 'sofZmanShma',
  'sofZmanTfillaMGA19Point8', 'sofZmanTfillaMGA16Point1', 'sofZmanTfillaMGA', 'sofZmanTfilla',
  'chatzot', 'minchaGedola', 'minchaGedolaMGA', 'minchaKetana', 'minchaKetanaMGA', 'plagHaMincha',
  'sunset', 'beinHaShmashos', 'dusk', 'tzeit7083deg', 'tzeit85deg', 'tzeit42min', 'tzeit50min', 'tzeit72min',
  // ZmanSelect dropdown keys (internal naming, slightly different from API)
  'netzHachama', 'plagHamincha', 'beinHashmashos',
  'sofZmanAchilatChametz', 'sofZmanBiurChametz', 'sofZmanAchilatChametzMGA', 'sofZmanBiurChametzMGA',
  'none',
  // MGA 16.1 variants returned alongside standard keys
  'sofZmanShmaMGA19Point8', 'sofZmanTfillaMGA19Point8',
  // Override keys we add internally in apiService
  'sofZmanShmaMGA', 'sofZmanTfillaMGA',
  // Keys we add internally for sentinel time display — not user-selectable options
  'eveSunset', 'dayEntryTime', 'dayExitTime', 'dayEntrySunset', 'dayExitSunset',
]);
// eveCandleLighting / eveFastBegins are intentionally NOT in KNOWN_STATIC_KEYS → appear in extraOptions

// תוויות לזמנים ייחודיים לתאריך (מ-Hebcal Calendar API — יום + ערב) + שיטת בעל התניא
const API_KEY_LABELS = {
  candleLighting:    'הדלקת נרות',
  havdalah:          'הבדלה',
  fastBegins:        'תחילת הצום',
  fastEnds:          'סיום הצום',
  eveCandleLighting: 'הדלקת נרות (מערב)',
  eveFastBegins:     'תחילת הצום (מערב)',
  // שיטת בעל התניא (חב"ד) — מוחזרת תמיד מ-Hebcal אך אינה ב-ZMAN_DEFINITIONS
  alosBaalHatanya:          'עלות השחר (בעל התניא)',
  sofZmanShmaBaalHatanya:   'סוף זמן שמע (בעל התניא)',
  sofZmanTfilaBaalHatanya:  'סוף זמן תפילה (בעל התניא)',
  minchaGedolaBaalHatanya:  'מנחה גדולה (בעל התניא)',
  minchaKetanaBaalHatanya:  'מנחה קטנה (בעל התניא)',
  plagHaminchaBaalHatanya:  'פלג המנחה (בעל התניא)',
  tzaisBaalHatanya:         'צאת הכוכבים (בעל התניא)',
};

const formatApiTime = (isoStr) => {
  if (!isoStr) return '';
  try {
    const s = String(isoStr);
    // already HH:MM formatted
    if (/^\d{2}:\d{2}$/.test(s)) return s;
    // extract from ISO string
    const m = s.match(/T(\d{2}):(\d{2})/);
    if (m) return `${m[1]}:${m[2]}`;
    return '';
  } catch { return ''; }
};

// הוסף/הפחת דקות ממחרוזת HH:MM
const addMinutes = (timeStr, mins) => {
  if (!timeStr || !String(timeStr).includes(':')) return null;
  const [h, m] = String(timeStr).split(':').map(Number);
  const total = h * 60 + m + Math.round(Number(mins) || 0);
  const wrapped = ((total % 1440) + 1440) % 1440;
  return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`;
};

// חשב שעות לאפשרויות sentinel לפי הגדרת כניסה/יציאה גלובלית
const computeSentinelTimes = (dayZmanim, entryZman) => {
  if (!dayZmanim) return {};
  const result = {
    dayExitTime:    dayZmanim.tzeit85deg  || null,
    dayEntrySunset: dayZmanim.eveSunset   || null,
    dayExitSunset:  dayZmanim.sunset      || null,
    dayEntryTime:   null,
  };
  if (entryZman?.key === 'sunset' && dayZmanim.eveSunset) {
    result.dayEntryTime = addMinutes(dayZmanim.eveSunset, -(Number(entryZman.minsBeforeSunset) || 0));
  } else if (entryZman?.key === 'plagHamincha' && dayZmanim.plagHamincha) {
    result.dayEntryTime = addMinutes(dayZmanim.plagHamincha, Number(entryZman.offset) || 0);
  }
  return result;
};

/**
 * ZmanRowCompact — שורה אחת לזמן: תווית + בורר זמן + offset + אייקון
 */
const ZmanRowCompact = ({ label, labelColor, value = {}, onChange, defaultKey, apiZmanim = {}, apiLoading = false, hebrewDay, hebrewMonth, dayZmanim = null }) => {
  const key    = value.key    ?? defaultKey;
  const offset = value.offset ?? 0;
  const icon   = value.icon   ?? (defaultKey === 'dayEntryTime' ? 'ShabbatCandles' : 'MoonStar');

  const isSentinel = MOAD_DEFAULT_KEYS.has(key);

  const handleOffset = (e) => {
    const raw = e.target.value;
    if (raw === '' || raw === '-') { onChange({ key, offset: raw, icon }); return; }
    const n = parseInt(raw, 10);
    if (!isNaN(n)) onChange({ key, offset: n, icon });
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {/* תווית */}
      <Typography sx={{
        fontSize: '0.75rem', fontWeight: 700,
        color: labelColor || '#374151',
        minWidth: 42, flexShrink: 0,
      }}>
        {label}
      </Typography>

      {/* בורר זמן */}
      <ZmanSelect
        value={key}
        onChange={(newKey) => onChange({ key: newKey, offset: 0, icon })}
        keyPrefix=""
        showSentinels
        extraOptions={Object.entries(apiZmanim)
          .filter(([k]) => !KNOWN_STATIC_KEYS.has(k))
          .map(([k, v]) => ({ key: k, label: API_KEY_LABELS[k] || k, time: formatApiTime(v) }))}
        hebrewDay={hebrewDay}
        hebrewMonth={hebrewMonth}
        showNone
        zmanimForDay={dayZmanim}
        disabled={apiLoading}
        sx={apiLoading ? { opacity: 0.7 } : {}}
      />

      {/* Offset */}
      <TextField
        size="small"
        value={isSentinel ? '' : offset}
        onChange={handleOffset}
        disabled={key === 'none' || isSentinel}
        placeholder={isSentinel ? '—' : '0'}
        inputProps={{ style: { textAlign: 'center', fontSize: '0.78rem', width: 24 } }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Typography sx={{ fontSize: '0.65rem', color: '#9ca3af' }}>דק׳</Typography>
            </InputAdornment>
          ),
        }}
        sx={{
          width: 70, flexShrink: 0,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
        }}
      />

      {/* אייקון */}
      <Select
        size="small"
        value={icon}
        onChange={(e) => onChange({ key, offset, icon: e.target.value })}
        disabled={key === 'none'}
        renderValue={(v) => <IconSelectItem iconValue={v} />}
        sx={{ ...selectSx, width: 100, flexShrink: 0 }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
      >
        {ICON_OPTIONS.map(opt => (
          <MenuItem key={opt.value} value={opt.value} sx={{ py: 0.6 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <opt.Icon size={15} color="#374151" />
              <Typography sx={{ fontSize: '0.8rem' }}>{opt.label}</Typography>
            </Stack>
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
};

/**
 * MoadZmanimPerDayStep
 * שלב משולב — לכל יום מועד: זמן א + זמן ב יחד בכרטיסייה אחת.
 *
 * הרעיון: שני זמנים גמישים לחלוטין — יכולים להיות כניסה/יציאה, ביעור חמץ, תענית, מגילה וכו׳.
 */
const MoadZmanimPerDayStep = ({ formData, onChange, onFetchZmanimForDay }) => {
  const days = formData?.days || [];
  const entryZman = formData?.entryZman; // הגדרת כניסה גלובלית (מ-MoadCalcStep)
  const [zmanimByDay, setZmanimByDay] = useState({});   // { dayIndex: { key: isoString, ... } }
  const [loadingByDay, setLoadingByDay] = useState({}); // { dayIndex: bool }

  useEffect(() => {
    if (!onFetchZmanimForDay || days.length === 0) return;
    days.forEach((day, i) => {
      if (!day.hebrewMonth || !day.hebrewDay) return;
      if (zmanimByDay[i] !== undefined) return; // already fetched
      setLoadingByDay(prev => ({ ...prev, [i]: true }));
      onFetchZmanimForDay(day.hebrewMonth, day.hebrewDay)
        .then(times => {
          setZmanimByDay(prev => ({ ...prev, [i]: times }));
        })
        .catch(() => {
          setZmanimByDay(prev => ({ ...prev, [i]: {} }));
        })
        .finally(() => {
          setLoadingByDay(prev => ({ ...prev, [i]: false }));
        });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days.length, onFetchZmanimForDay]);

  const updateDay = (dayIndex, partial) => {
    const updated = days.map((day, i) => i === dayIndex ? { ...day, ...partial } : day);
    onChange({ days: updated });
  };

  if (days.length === 0) {
    return (
      <Box dir="rtl" sx={{ textAlign: 'center', py: 4 }}>
        <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem' }}>
          לא הוגדרו ימי מועד — חזור לשלב הקודם והוסף ימים.
        </Typography>
      </Box>
    );
  }

  return (
    <Box dir="rtl">
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
        זמן א׳ וזמן ב׳ לכל יום
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 1.5, lineHeight: 1.65 }}>
        לכל יום מועד ניתן להציג שני זמנים הלכתיים — בדרך כלל כניסת ויציאת מועד, אך גם כל זמן אחר
        שתרצה: ביעור חמץ, תחילת תענית, זמן קריאת מגילה, סוף זמן תפילה וכד׳.
        בחר אייקון שמתאים לאופי הזמן.
      </Typography>

      {/* כותרות עמודות */}
      <Stack direction="row" spacing={1} sx={{ mb: 0.75, pr: 0.5 }}>
        <Box sx={{ minWidth: 42 }} />
        <Typography sx={{ flex: 1, fontSize: '0.68rem', color: '#9ca3af', fontWeight: 600 }}>זמן הלכתי</Typography>
        <Typography sx={{ width: 70, flexShrink: 0, fontSize: '0.68rem', color: '#9ca3af', fontWeight: 600, textAlign: 'center' }}>offset</Typography>
        <Typography sx={{ width: 100, flexShrink: 0, fontSize: '0.68rem', color: '#9ca3af', fontWeight: 600 }}>אייקון</Typography>
      </Stack>

      <Stack spacing={2}>
        {days.map((day, i) => (
          <Box key={i} sx={{
            bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}>
            {/* שם היום */}
            <Typography sx={{ fontWeight: 700, color: navy, fontSize: '0.88rem', mb: 1.5 }}>
              {day.name || day.label || `יום ${i + 1}`}
            </Typography>

            <Stack spacing={1.25}>
              <ZmanRowCompact
                label="זמן א׳"
                labelColor="#166634"
                value={day.entryZman}
                defaultKey="dayEntryTime"
                onChange={(zv) => updateDay(i, { entryZman: zv })}
                apiZmanim={zmanimByDay[i] || {}}
                apiLoading={!!loadingByDay[i]}
                hebrewDay={day.hebrewDay}
                hebrewMonth={day.hebrewMonth}
                dayZmanim={zmanimByDay[i] ? { ...zmanimByDay[i], ...computeSentinelTimes(zmanimByDay[i], entryZman) } : null}
              />
              <Divider sx={{ borderColor: '#f3f4f6' }} />
              <ZmanRowCompact
                label="זמן ב׳"
                labelColor="#1e3a5f"
                value={day.exitZman}
                defaultKey="dayExitTime"
                onChange={(zv) => updateDay(i, { exitZman: zv })}
                apiZmanim={zmanimByDay[i] || {}}
                apiLoading={!!loadingByDay[i]}
                hebrewDay={day.hebrewDay}
                hebrewMonth={day.hebrewMonth}
                dayZmanim={zmanimByDay[i] ? { ...zmanimByDay[i], ...computeSentinelTimes(zmanimByDay[i], entryZman) } : null}
              />
            </Stack>
          </Box>
        ))}
      </Stack>

      <Box sx={{ bgcolor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 2, px: 2, py: 1.25, mt: 2 }}>
        <Typography sx={{ fontSize: '0.74rem', color: '#0c4a6e', lineHeight: 1.6 }}>
          <strong>offset</strong> — דקות להוסיף/לחסר מהזמן הבסיסי (למשל ‎−30 = חצי שעה לפני). שדה זה מושבת עבור אפשרויות "לפי הגדרה גלובלית".
          <br />
          אם התאריך הוא ערב יום טוב / ערב יום כיפור — תופיע <strong>הדלקת נרות</strong> בראש הרשימה עם השעה המדויקת (= תחילת הצום לגבי יום כיפור).
          אם התאריך הוא יום כיפור / יום טוב עצמו — תופיע <strong>הבדלה</strong> עם השעה המדויקת (= סיום הצום).
          <br />
          שקיעה וצאת כוכבים נשאבים מ<strong>ערב יום המועד</strong>; זמני יום (נץ, חצות, מנחה...) נשאבים מ<strong>יום המועד עצמו</strong>.
        </Typography>
      </Box>
    </Box>
  );
};

export default MoadZmanimPerDayStep;
